import {
  DeleteOutlineIcon as DeleteOutline,
  ErrorOutlineIcon as ErrorOutline,
  Image,
  ImagePlaceholder,
  Tooltip,
  Typography,
} from "@iluvatar/ui-kit";

import { useState } from "react";

import { IFileReference } from "@iluvatar/global/src/typings";

import { AspectRatio } from "@/components/ui/aspect-ratio";
import { useObjectUrl } from "@/hooks/useObjectUrl";
import { cn } from "@/utilities";

import { useStorageUrl } from "../../hooks/useStorageUrl";
import {
  imgRefAvif,
  imgRefSize,
} from "../../services/file/file-to-ref-utilities";
import { ProcessingImage } from "./processing-image";

function TrashIcon(props: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      {...props}
      className={cn(
        props.className,
        "flex flex-col place-items-center justify-center rounded-[15px]",
        "absolute left-0 right-0 top-0 bottom-0 bg-black/[0.8] z-[100] text-white/[0.7]",
        "cursor-pointer opacity-0 hover:opacity-100",
      )}
    >
      <DeleteOutline />
      <Typography variant="caption">Remove</Typography>
    </div>
  );
}

export function ImageAvatar({
  imgRef,
  communityId,
  blurred = false,
  className,
}: {
  imgRef?: IFileReference;
  communityId: string;
  blurred?: boolean;
} & React.ImgHTMLAttributes<HTMLImageElement>) {
  const [fileUrl] = useStorageUrl(
    imgRefSize(imgRef, 40, blurred),
    Boolean(imgRef?.metadata.isPrivate && !blurred),
    communityId,
  );

  return (
    <img
      src={fileUrl}
      draggable={false}
      className={cn("w-12 h-12 rounded-full", className)}
    />
  );
}

export function ImageRef({
  imgRef,
  communityId,
  blurred = false,
  file,
  children,
  onRemove,
  ...rest
}: {
  file?: File;
  imgRef?: IFileReference;
  communityId: string;
  blurred?: boolean;
  children?: React.ReactNode;
  onRemove?: () => void;
} & React.ImgHTMLAttributes<HTMLImageElement>) {
  const [fileUrl, loading, error] = useStorageUrl(
    imgRefSize(imgRef, 300, blurred),
    Boolean(imgRef?.metadata.isPrivate && !blurred),
    communityId,
  );
  const localUrl = useObjectUrl(file);
  const imageUrl = localUrl ?? fileUrl;

  if (imgRef?.metadata.isProcessing && !file) {
    return (
      <ProcessingImage>
        {onRemove && <TrashIcon onClick={onRemove} />}
      </ProcessingImage>
    );
  }

  if (error && !file) {
    return (
      <Tooltip content={error.message}>
        <ErrorOutline className="[font-size:26px] [color:var(--color-chart--overdue--dark)]" />
      </Tooltip>
    );
  }

  return (
    <>
      <AspectRatio ratio={1}>
        {onRemove && <TrashIcon onClick={onRemove} />}
        {loading && !file ? (
          <ImagePlaceholder className="max-w-[300px]" />
        ) : (
          <Image
            src={imageUrl}
            {...rest}
            className={cn("max-w-[300px]", rest.className)}
          />
        )}
      </AspectRatio>
      {children}
    </>
  );
}

export function ImageFull({
  imgRef,
  communityId,
  blurred = false,
  className,
  ...rest
}: {
  imgRef?: IFileReference;
  communityId: string;
  blurred?: boolean;
} & React.ImgHTMLAttributes<HTMLImageElement>) {
  const avif = blurred ? undefined : imgRefAvif(imgRef);
  const [failedAvif, setFailedAvif] = useState<string>();
  const useAvif = Boolean(avif && avif !== failedAvif);
  const [imageUrl, loading, error] = useStorageUrl(
    useAvif ? avif : imgRefSize(imgRef, blurred ? 300 : 600, blurred),
    Boolean(imgRef?.metadata.isPrivate && !blurred),
    communityId,
  );
  if (error && useAvif) {
    setFailedAvif(avif);
  }

  if (imgRef?.metadata.isProcessing) {
    return <ProcessingImage />;
  }

  if (error) {
    return (
      <Tooltip content={error.message}>
        <ErrorOutline className="[font-size:26px] [color:var(--color-chart--overdue--dark)]" />
      </Tooltip>
    );
  }

  return (
    <AspectRatio ratio={1}>
      {loading ? (
        <ImagePlaceholder rounded={false} className={className} />
      ) : (
        <Image
          rounded={false}
          src={imageUrl}
          {...rest}
          onError={(event) => {
            if (useAvif) {
              setFailedAvif(avif);
            } else {
              rest.onError?.(event);
            }
          }}
          className={cn("image-full", className)}
        />
      )}
    </AspectRatio>
  );
}
